"use client";

import Link from "next/link";
import { ArrowRight, Check, XCircle } from "lucide-react";

import { ADVISOR_AUTH_PRIMARY_CTA_GRADIENT } from "@/app/components/marketing/marketing-constants";
import {
  annualSavingsVersusTwelveMonthly,
  effectiveMonthlyKcWhenBilledAnnually,
  formatPublicPriceKc,
  yearlyTotalKcFromMonthlyList,
} from "@/lib/billing/public-pricing";

export type MarketingPricingCardVariant = "dark" | "light";

export function MarketingPricingCard({
  variant,
  name,
  tagline,
  monthly,
  annual,
  includes,
  excludes,
  featured,
  featuredBadgeDark = "Nejčastější volba",
  featuredBadgeLight = "Nejčastější volba",
  primaryHref,
  primaryLabel,
  showSecondaryLink,
  secondaryHref = "/prihlaseni",
  secondaryLabel = "Už mám účet — přihlásit se",
  /** Na světlém pozadí sekce (/tarify): stejné tmavé karty jako homepage, bez skleněného 3% fillu. */
  onLightSurface,
}: {
  variant: MarketingPricingCardVariant;
  onLightSurface?: boolean;
  name: string;
  tagline: string;
  monthly: number;
  annual: boolean;
  includes: readonly string[];
  excludes?: readonly string[];
  featured?: boolean;
  featuredBadgeDark?: string;
  featuredBadgeLight?: string;
  primaryHref: string;
  primaryLabel: string;
  showSecondaryLink?: boolean;
  secondaryHref?: string;
  secondaryLabel?: string;
}) {
  const displayedPrice = annual ? effectiveMonthlyKcWhenBilledAnnually(monthly) : monthly;
  const yearlyTotal = yearlyTotalKcFromMonthlyList(monthly);
  const yearlySavings = annualSavingsVersusTwelveMonthly(monthly);

  const dphLine =
    variant === "dark" ? (
      <p className="mt-3 text-[11px] font-medium leading-relaxed text-slate-500">Cena je konečná. Nejsme plátci DPH.</p>
    ) : (
      <p className="mt-3 text-xs font-semibold leading-relaxed text-slate-600">Cena je konečná. Nejsme plátci DPH.</p>
    );

  if (variant === "dark") {
    const standardDarkShell = onLightSurface
      ? "border border-slate-900/10 bg-[#0a0f29] shadow-[0_24px_80px_rgba(15,23,42,0.14)] transition-colors hover:shadow-[0_28px_90px_rgba(15,23,42,0.18)]"
      : "border border-white/10 bg-white/[0.03] transition-colors hover:bg-white/[0.06]";

    const card = (
      <div
        className={`relative flex h-full flex-col rounded-[28px] p-7 md:p-8 ${
          featured ? "bg-[#0a0f29]" : standardDarkShell
        }`}
      >
        {featured ? (
          <div
            className="absolute left-1/2 top-0 z-20 -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-500 px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-white shadow-[0_8px_24px_-8px_rgba(99,102,241,0.55)]"
          >
            {featuredBadgeDark}
          </div>
        ) : null}

        <h3 className="mb-1 font-jakarta text-xl font-bold text-white md:text-2xl">{name}</h3>
        <p className="mb-5 text-sm text-slate-400">{tagline}</p>

        <div className="mb-1 flex items-baseline gap-1">
          <span className="font-black tabular-nums text-4xl text-white md:text-5xl">{formatPublicPriceKc(displayedPrice)}</span>
          <span className="text-sm font-medium text-slate-500">Kč / měs.</span>
        </div>
        <p className="mb-1 text-[11px] font-bold uppercase tracking-widest text-slate-500">
          {annual ? "Ekvivalent při roční fakturaci" : "Fakturováno měsíčně"}
        </p>
        {annual ? (
          <p className="mb-3 text-[11px] text-slate-500">
            Celkem {formatPublicPriceKc(yearlyTotal)} Kč / rok · úspora {formatPublicPriceKc(yearlySavings)} Kč
          </p>
        ) : (
          <div className="mb-3" />
        )}
        {dphLine}

        <Link
          href={primaryHref}
          className={`mb-2 mt-5 inline-flex min-h-[48px] w-full items-center justify-center rounded-xl px-5 py-3 text-sm font-bold ${
            featured
              ? `${ADVISOR_AUTH_PRIMARY_CTA_GRADIENT} shadow-[0_10px_30px_-10px_rgba(99,102,241,0.55)]`
              : "border border-white/10 bg-white/10 text-white transition-colors hover:bg-white/20"
          }`}
        >
          {primaryLabel}
        </Link>
        {showSecondaryLink ? (
          <Link
            href={secondaryHref}
            className={`mb-6 block w-full py-2.5 text-center text-sm font-medium transition-colors ${
              featured ? "text-indigo-200/90 hover:text-white" : "text-slate-400 hover:text-white"
            }`}
          >
            {secondaryLabel}
          </Link>
        ) : (
          <div className="mb-6" />
        )}

        <ul className="mb-5 space-y-2.5">
          {includes.map((line) => (
            <li
              key={line}
              className={`flex items-start gap-2.5 text-sm ${featured ? "font-medium text-white" : "text-slate-300"}`}
            >
              <Check size={17} className={`mt-0.5 shrink-0 ${featured ? "text-emerald-400" : "text-indigo-400"}`} />
              {line}
            </li>
          ))}
        </ul>

        {excludes && excludes.length > 0 ? (
          <div className="mt-auto border-t border-white/10 pt-4">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">V ceně Start nejsou</p>
            <ul className="space-y-1.5">
              {excludes.map((line) => (
                <li key={line} className="flex items-start gap-2.5 text-xs text-slate-500">
                  <XCircle size={14} className="mt-0.5 shrink-0 text-slate-600" /> {line}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    );

    if (featured) {
      return (
        <div className="pro-pricing-wrapper h-full w-full min-w-0 overflow-visible pt-4 shadow-[0_30px_60px_-30px_rgba(139,92,246,0.45)]">
          <div className="pro-pricing-inner">{card}</div>
        </div>
      );
    }
    return card;
  }

  /* light variant — tarify */
  const inner = (
    <>
      {featured ? (
        <div className="mb-5 inline-flex w-fit rounded-full bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.16em] text-indigo-700">
          {featuredBadgeLight}
        </div>
      ) : (
        <div className="mb-5 h-6" />
      )}

      <div>
        <p className={`text-sm font-black uppercase tracking-[0.2em] ${featured ? "text-indigo-200" : "text-indigo-700"}`}>
          {name}
        </p>

        <div className="mt-4 flex items-end gap-1">
          <span className="text-5xl font-black tracking-[-0.07em]">{formatPublicPriceKc(displayedPrice)}</span>
          <span className={`pb-1 text-base font-bold ${featured ? "text-slate-300" : "text-slate-500"}`}>Kč&nbsp;/&nbsp;měs.</span>
        </div>

        {billingLine(annual, yearlyTotal, yearlySavings, Boolean(featured))}
        {dphLine}

        <p className={`mt-5 text-sm font-semibold leading-6 ${featured ? "text-slate-300" : "text-slate-600"}`}>{tagline}</p>
      </div>

      <div className={`my-6 h-px ${featured ? "bg-white/10" : "bg-slate-100"}`} />

      <ul className="space-y-3">
        {includes.map((line) => (
          <li key={line} className="flex gap-3 text-sm font-semibold leading-6">
            <span
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                featured ? "bg-indigo-300 text-slate-950" : "bg-emerald-50 text-emerald-700"
              }`}
            >
              <Check size={12} />
            </span>
            <span>{line}</span>
          </li>
        ))}
      </ul>

      {excludes?.length ? (
        <div className="mt-6 mb-6 rounded-3xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold leading-6 text-amber-950">
          <p className="font-black">Transparentně u Startu:</p>
          {excludes.map((item) => (
            <p key={item}>&#8226; {item}</p>
          ))}
        </div>
      ) : (
        <div className="mb-2" />
      )}

      <Link
        href={primaryHref}
        className={`mt-auto inline-flex h-12 min-h-[48px] items-center justify-center rounded-2xl px-5 text-sm font-black transition ${ADVISOR_AUTH_PRIMARY_CTA_GRADIENT} shadow-[0_8px_24px_-8px_rgba(99,102,241,0.45)]`}
      >
        {primaryLabel}
        <ArrowRight size={16} className="ml-2" />
      </Link>
    </>
  );

  if (featured) {
    return (
      <div className="h-full w-full min-w-0 overflow-hidden rounded-[34px] bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-500 p-[2px] shadow-[0_24px_80px_rgba(79,70,229,0.38)]">
        <article className="relative flex h-full min-h-full flex-col rounded-[31px] bg-slate-950 p-6 text-white">{inner}</article>
      </div>
    );
  }

  return (
    <article className="relative flex h-full flex-col rounded-[32px] border border-slate-200 bg-white p-6 text-slate-950 shadow-[0_24px_80px_rgba(15,23,42,0.07)]">
      {inner}
    </article>
  );
}

function billingLine(annual: boolean, yearlyTotal: number, saving: number, featured: boolean) {
  if (annual) {
    return (
      <p className={`mt-2 text-sm font-semibold ${featured ? "text-indigo-100" : "text-slate-600"}`}>
        Ročně {formatPublicPriceKc(yearlyTotal)} Kč. Úspora {formatPublicPriceKc(saving)} Kč oproti měsíční platbě.
      </p>
    );
  }
  return (
    <p className={`mt-2 text-sm font-semibold ${featured ? "text-slate-300" : "text-slate-600"}`}>Měsíční fakturace bez závazku.</p>
  );
}
